import { useCallback, useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  coachPostSessionApi,
  type PostSessionBundle,
  type PostSessionCommitment,
} from '../services/api/coachPostSession';
import { useAiInsightPolling } from './useAiInsightPolling';
import {
  coachPortalApi,
  type AbsentReason,
  type AttendanceMarkingStatus,
  type CreateConcernFlagInput,
  type EngagementRatingInput,
  type ParticipantAttendanceInput,
} from '../services/api/coachPortal';

/**
 * MED-794 / 2.6a — data + mutation hook for the post-session screen.
 *
 * Owns four pieces of state:
 *   1. The fetched bundle (read-once on mount, refreshed after mutations
 *      that the screen depends on rendering).
 *   2. Attendance & engagement working copy (per-participant editable state
 *      surfaced by AttendanceEngagementCard until the coach saves).
 *   3. Feedback responses working copy (Medley Feedback Form).
 *   4. Commitments (Log Commitments card) — MED-1216: explicit per-row save
 *      (no more debounced whole-roster flush). A coach save targeting a
 *      participant-authored row is rejected server-side (409); the hook then
 *      refreshes the bundle and surfaces a per-row notice (AC7).
 *
 * Attendance / engagement / feedback submissions use the existing coachPortal
 * endpoints. Commitments use the new coachPostSessionApi.
 */

export interface AttendanceDraft {
  status: AttendanceMarkingStatus | null;
  absent_reason: AbsentReason | null;
  rating: number | null;
  // MED-979 AC5: per-participant free-text note. Shown for all three statuses.
  coach_notes: string | null;
  // MED-1191 AC8: seeded from the server's derived status_auto_populated. Marks
  // a status that was auto-inferred from the heartbeat and not yet reviewed;
  // drives the "Review" flag on auto-partial rows and clears the moment the
  // leader picks any status (they've reviewed it).
  autoPopulated: boolean;
}

export type AttendanceDraftMap = Record<string, AttendanceDraft>;
export type FeedbackResponseMap = Record<string, string>;

interface UseCoachPostSessionResult {
  bundle: PostSessionBundle | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;

  attendance: AttendanceDraftMap;
  setAttendanceForParticipant: (participantId: string, next: AttendanceDraft) => void;
  saveAttendance: () => Promise<boolean>;
  savingAttendance: boolean;

  feedbackResponses: FeedbackResponseMap;
  setFeedbackResponse: (key: string, value: string) => void;
  saveFeedbackDraft: () => Promise<boolean>;
  submitFeedback: () => Promise<boolean>;
  savingFeedback: boolean;
  feedbackSubmittedAt: string | null;

  // MED-1035 — participant concern flags bundled into the feedback save/submit
  // payload (concern_flags[]). Seeded from existing flags on load so a re-submit
  // does not wipe them (updateFeedback replaces flags via delete-then-insert).
  concernFlags: CreateConcernFlagInput[];
  setConcernFlags: (flags: CreateConcernFlagInput[]) => void;

  // MED-1216 — server-truth commitment rows + explicit per-row save.
  commitments: PostSessionCommitment[];
  saveCommitment: (participantId: string, text: string) => Promise<boolean>;
  savingParticipantId: string | null;
  commitmentNotices: Record<string, string>;
}

/**
 * Map the participant row's persisted `current_status` to the three-way
 * working-copy enum the UI uses. Anything that isn't 'attended' / 'partial'
 * / 'absent' is treated as "not set yet" (null).
 */
function toAttendanceMarking(status: string): AttendanceMarkingStatus | null {
  if (status === 'attended' || status === 'partial' || status === 'absent') return status;
  if (status === 'present') return 'attended';
  return null;
}

export function useCoachPostSession(
  sessionId: string | null,
  options: { previewBundle?: PostSessionBundle } = {},
): UseCoachPostSessionResult {
  // MED-1040 — DEV preview seam: when a bundle is supplied, seed all working
  // copies from it and skip the network entirely (no production impact).
  const previewBundle = options.previewBundle;
  const [bundle, setBundle] = useState<PostSessionBundle | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [attendance, setAttendance] = useState<AttendanceDraftMap>({});
  const [feedbackResponses, setFeedbackResponses] = useState<FeedbackResponseMap>({});
  const [concernFlags, setConcernFlags] = useState<CreateConcernFlagInput[]>([]);

  const [savingAttendance, setSavingAttendance] = useState<boolean>(false);
  const [savingFeedback, setSavingFeedback] = useState<boolean>(false);
  // MED-1216 — which participant's commitment row is saving, plus per-row
  // notices set after a coach save is rejected as participant-owned (AC7).
  const [savingParticipantId, setSavingParticipantId] = useState<string | null>(null);
  const [commitmentNotices, setCommitmentNotices] = useState<Record<string, string>>({});

  // ---------------------------------------------------------------------------
  // Initial load + refresh
  // ---------------------------------------------------------------------------

  const loadBundle = useCallback(async () => {
    // Seed the editable working copies from a bundle — shared by the live
    // fetch and the DEV preview.
    const applyBundle = (data: PostSessionBundle) => {
      setBundle(data);
      const attMap: AttendanceDraftMap = {};
      const ratingByParticipant = new Map<string, number>(
        data.engagement_ratings.map((r) => [r.participant_id, r.rating]),
      );
      for (const p of data.participants) {
        attMap[p.user_id] = {
          status: toAttendanceMarking(p.current_status),
          absent_reason: p.current_absent_reason,
          rating: ratingByParticipant.get(p.user_id) ?? null,
          coach_notes: p.current_coach_notes,
          autoPopulated: p.status_auto_populated,
        };
      }
      setAttendance(attMap);

      const fb: FeedbackResponseMap = {};
      if (data.feedback?.responses) {
        for (const [k, v] of Object.entries(data.feedback.responses)) {
          fb[k] = v;
        }
      }
      setFeedbackResponses(fb);
      // MED-1216 — commitment rows are read directly from `bundle.commitments`
      // (server truth); the card owns per-row edit drafts. No draft map to seed.
    };

    // DEV preview: seed from the fixture, skip the network entirely.
    if (previewBundle) {
      applyBundle(previewBundle);
      setConcernFlags([]);
      setError(null);
      setLoading(false);
      return;
    }

    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      // MED-1035: fetch existing concern flags alongside the bundle (the bundle
      // does not carry them). Seeding them mirrors the legacy drawer and is
      // required for correctness: updateFeedback replaces a coach's flags via
      // delete-then-insert, so submitting with an empty array would wipe flags
      // created earlier. Mirrors PostSessionModal's getSessionFeedback seeding.
      const [data, feedbackDetail] = await Promise.all([
        coachPostSessionApi.getBundle(sessionId),
        coachPortalApi.getSessionFeedback(sessionId),
      ]);
      setConcernFlags(
        feedbackDetail.concernFlags.map((f) => ({
          participant_id: f.participant_id,
          concern_type: f.concern_type,
          notes: f.notes || undefined,
        })),
      );
      applyBundle(data);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load post-session data';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [sessionId, previewBundle]);

  useEffect(() => {
    void loadBundle();
  }, [loadBundle]);

  // ---------------------------------------------------------------------------
  // MED-1239 — auto-resolve async AI insights without a hard refresh
  // ---------------------------------------------------------------------------

  /**
   * Refetch the bundle and merge ONLY the AI insight envelopes (Session Themes,
   * Airtime Balance) into state. Deliberately does NOT go through `applyBundle`
   * / `loadBundle`: those reseed the editable working copies (attendance,
   * feedback, concern flags) from server truth, which would clobber a coach's
   * in-progress edits on every poll tick (AC5). Merging just `themes` + `airtime`
   * onto the existing bundle re-renders the two cards in place and leaves every
   * editable surface untouched. Poll failures are swallowed — a transient blip
   * must not surface an error toast or blank the page; the next tick (or a
   * natural load) retries.
   */
  const pollAiEnvelopes = useCallback(async () => {
    if (!sessionId) return;
    try {
      const fresh = await coachPostSessionApi.getBundle(sessionId);
      setBundle((prev) =>
        prev ? { ...prev, themes: fresh.themes, airtime: fresh.airtime } : prev,
      );
    } catch {
      // Intentionally ignored — see doc comment.
    }
  }, [sessionId]);

  // Poll while EITHER pending-capable envelope this view renders is still
  // generating (Themes + Airtime — AC2). Terminal states stop the poll (AC3),
  // bounded to the backend pending window (AC4). Disabled in DEV preview.
  const aiInsightsPending =
    bundle?.themes?.status === 'pending' || bundle?.airtime?.status === 'pending';
  useAiInsightPolling({
    enabled: !previewBundle && !!sessionId,
    pending: !!aiInsightsPending,
    poll: pollAiEnvelopes,
  });

  // ---------------------------------------------------------------------------
  // Attendance & engagement
  // ---------------------------------------------------------------------------

  const setAttendanceForParticipant = useCallback(
    (participantId: string, next: AttendanceDraft) => {
      setAttendance((prev) => ({ ...prev, [participantId]: next }));
    },
    [],
  );

  const saveAttendance = useCallback(async (): Promise<boolean> => {
    if (!sessionId || !bundle) return false;

    const participants = bundle.participants;
    const records: ParticipantAttendanceInput[] = [];
    const ratings: EngagementRatingInput[] = [];

    for (const p of participants) {
      const draft = attendance[p.user_id];
      if (!draft || !draft.status) continue;
      const trimmedNotes = draft.coach_notes?.trim() ?? '';
      records.push({
        user_id: p.user_id,
        status: draft.status,
        // MED-979 AC2: absent_reason applies to Partial AND Absent. Send null
        // for Present so AC7 clears the now-irrelevant column on save.
        absent_reason:
          draft.status === 'partial' || draft.status === 'absent'
            ? draft.absent_reason
            : null,
        // MED-979 AC5: coach_notes apply to all three statuses. Empty / null
        // is sent as explicit null so AC7 clears the column on save.
        coach_notes: trimmedNotes === '' ? null : trimmedNotes,
      });
      // Engagement rating is only meaningful when the participant attended in
      // some form. AC2: shown when Present or Partial selected.
      if (
        (draft.status === 'attended' || draft.status === 'partial') &&
        draft.rating !== null &&
        draft.rating >= 1 &&
        draft.rating <= 5
      ) {
        ratings.push({ participant_id: p.user_id, rating: draft.rating });
      }
    }

    if (records.length === 0) {
      notifications.show({
        color: 'yellow',
        message: 'Select attendance for at least one participant before saving.',
      });
      return false;
    }

    setSavingAttendance(true);
    try {
      await coachPortalApi.markAttendance(sessionId, {
        attendance: records,
        send_surveys: true,
        engagement_ratings: ratings,
      });
      notifications.show({ color: 'green', message: 'Attendance saved.' });
      await loadBundle();
      return true;
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof Error ? e.message : 'Failed to save attendance.',
      });
      return false;
    } finally {
      setSavingAttendance(false);
    }
  }, [sessionId, bundle, attendance, loadBundle]);

  // ---------------------------------------------------------------------------
  // Feedback form
  // ---------------------------------------------------------------------------

  const setFeedbackResponse = useCallback((key: string, value: string) => {
    setFeedbackResponses((prev) => ({ ...prev, [key]: value }));
  }, []);

  const persistFeedback = useCallback(
    async (asDraft: boolean): Promise<boolean> => {
      if (!sessionId || !bundle) return false;
      setSavingFeedback(true);
      try {
        // MED-867: carry the draft-vs-submit intent to the API. Save Draft
        // (asDraft=true) persists responses but leaves sessions.feedback_submitted
        // false, so the dashboard "Submit Session Feedback" card stays until the
        // coach actually submits (AC3 / IV3). POST when no row exists yet, PUT
        // once a (possibly drafted) row exists — including to submit it.
        // MED-1035: bundle concern flags into the save/submit payload so flags
        // created on the MED02 page are persisted to participant_concern_flags
        // (status `open`) — the same path the legacy drawer uses (AC9).
        const payload = {
          responses: feedbackResponses,
          concern_flags: concernFlags,
          submitted: !asDraft,
        };
        if (bundle.feedback) {
          await coachPortalApi.updateSessionFeedback(sessionId, payload);
        } else {
          await coachPortalApi.submitSessionFeedback(sessionId, payload);
        }
        notifications.show({
          color: 'green',
          message: asDraft ? 'Feedback draft saved.' : 'Feedback submitted.',
        });
        await loadBundle();
        return true;
      } catch (e) {
        notifications.show({
          color: 'red',
          message: e instanceof Error ? e.message : 'Failed to save feedback.',
        });
        return false;
      } finally {
        setSavingFeedback(false);
      }
    },
    [sessionId, bundle, feedbackResponses, concernFlags, loadBundle],
  );

  const saveFeedbackDraft = useCallback(() => persistFeedback(true), [persistFeedback]);
  const submitFeedback = useCallback(() => persistFeedback(false), [persistFeedback]);

  // ---------------------------------------------------------------------------
  // Commitments — explicit per-row save (MED-1216)
  // ---------------------------------------------------------------------------

  /**
   * Save one participant's coach commitment (AC6 — never touches other rows).
   *
   * On success the single returned row is reconciled into `bundle.commitments`.
   * A 409 means the participant saved their own commitment in the meantime
   * (AC4): the coach cannot overwrite it, so we refresh the bundle to show the
   * participant's entry and set an AC7 notice for that row. The empty case is
   * blocked by the disabled Save button; the server rejects it too (400).
   */
  const saveCommitment = useCallback(
    async (participantId: string, text: string): Promise<boolean> => {
      if (!sessionId) return false;
      setSavingParticipantId(participantId);
      // Clear any stale notice for this row before attempting the save.
      setCommitmentNotices((prev) => {
        if (!(participantId in prev)) return prev;
        const next = { ...prev };
        delete next[participantId];
        return next;
      });
      try {
        const response = await coachPostSessionApi.upsertCommitments(sessionId, {
          commitments: [{ participant_user_id: participantId, commitment_text: text }],
        });
        setBundle((prev) =>
          prev ? { ...prev, commitments: response.commitments as PostSessionCommitment[] } : prev,
        );
        return true;
      } catch (e) {
        const status = (e as { status?: number }).status;
        if (status === 409) {
          // AC7 — participant now owns the row. Refresh to their saved entry and
          // explain why the coach's edit didn't take.
          await loadBundle();
          setCommitmentNotices((prev) => ({
            ...prev,
            [participantId]:
              'This participant saved their own commitment, so your entry was not applied. Their commitment is shown above.',
          }));
        } else {
          notifications.show({
            color: 'red',
            message: e instanceof Error ? e.message : 'Failed to save commitment.',
          });
        }
        return false;
      } finally {
        setSavingParticipantId(null);
      }
    },
    [sessionId, loadBundle],
  );

  // ---------------------------------------------------------------------------
  // Public surface
  // ---------------------------------------------------------------------------

  return {
    bundle,
    loading,
    error,
    refresh: loadBundle,

    attendance,
    setAttendanceForParticipant,
    saveAttendance,
    savingAttendance,

    feedbackResponses,
    setFeedbackResponse,
    saveFeedbackDraft,
    submitFeedback,
    savingFeedback,
    // MED-867: derive "submitted" from the session flag, not the feedback row's
    // submitted_at (which defaults to now() even for a draft). A drafted-but-
    // unsubmitted form now correctly reads as "Draft — not yet submitted".
    feedbackSubmittedAt: bundle?.session?.feedback_submitted
      ? bundle.session.feedback_submitted_at
      : null,

    concernFlags,
    setConcernFlags,

    commitments: bundle?.commitments ?? [],
    saveCommitment,
    savingParticipantId,
    commitmentNotices,
  };
}
